import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Save, Image as ImageIcon, Plus, Trash2, Edit3, Award, X, ExternalLink } from "lucide-react";

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const TIERS = [
  { v: "or", label: "Or" },
  { v: "argent", label: "Argent" },
  { v: "bronze", label: "Bronze" },
  { v: "custom", label: "Catégorie libre" },
];

const empty = {
  name: "",
  url: "",
  logo_base64: null,
  tier: "bronze",
  category: "",
  sort_order: 0,
  published: true,
};

const SponsorsPanel = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const refresh = () => {
    api.get("/admin/sponsors").then((r) => setItems(r.data)).catch(() => setItems([]));
  };

  useEffect(() => {
    refresh();
  }, []);

  const remove = async (id) => {
    if (!confirm("Supprimer ce commanditaire ?")) return;
    try {
      await api.delete(`/admin/sponsors/${id}`);
      refresh();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const grouped = items.reduce((acc, s) => {
    const k = s.tier === "custom" ? `custom:${s.category || "Autres"}` : s.tier;
    acc[k] = acc[k] || [];
    acc[k].push(s);
    return acc;
  }, {});

  return (
    <div data-testid="admin-sponsors-panel">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <span className="font-condensed uppercase tracking-[0.3em] text-xs text-[#E60012]">Partenaires</span>
          <h2 className="mt-1 font-display text-4xl uppercase">Commanditaires</h2>
          <p className="mt-1 text-white/55 text-sm">Niveaux Or / Argent / Bronze ou catégorie libre.</p>
        </div>
        <button
          onClick={() => setEditing({ ...empty })}
          data-testid="add-sponsor-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#E60012] hover:bg-[#ff1a30] font-condensed uppercase tracking-[0.2em] text-xs clip-tab"
        >
          <Plus className="w-4 h-4" /> Ajouter commanditaire
        </button>
      </div>

      {items.length === 0 ? (
        <div className="bg-[#111] border border-white/10 p-12 text-center">
          <Award className="w-10 h-10 text-[#E60012] mx-auto mb-3" />
          <div className="font-display text-2xl uppercase">Aucun commanditaire</div>
          <p className="mt-2 text-white/55 text-sm">Cliquez sur « Ajouter » pour publier le premier partenaire.</p>
        </div>
      ) : (
        Object.entries(grouped).map(([key, list]) => {
          const label = key.startsWith("custom:") ? key.slice(7) : TIERS.find((t) => t.v === key)?.label || key;
          return (
            <div key={key} className="mb-8" data-testid={`admin-tier-${key}`}>
              <div className="flex items-center gap-3 mb-3">
                <Award className="w-4 h-4 text-[#E60012]" />
                <h3 className="font-display text-2xl uppercase">{label}</h3>
                <span className="text-white/40 text-xs font-condensed uppercase tracking-wider">({list.length})</span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {list.map((s) => (
                  <div key={s.id} className="bg-[#111] border border-white/10 p-3 flex gap-3 items-center" data-testid={`admin-sponsor-${s.id}`}>
                    <div className="w-16 h-16 bg-black border border-white/10 flex items-center justify-center overflow-hidden shrink-0">
                      {s.logo_base64 ? (
                        <img src={s.logo_base64} alt={s.name} className="max-w-full max-h-full object-contain p-1" />
                      ) : (
                        <Award className="w-6 h-6 text-[#E60012]/50" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-condensed truncate">{s.name}</div>
                      <div className="text-[10px] text-white/50 uppercase tracking-wider font-condensed flex items-center gap-1">
                        <span className={s.published ? "text-emerald-400" : "text-white/40"}>
                          {s.published ? "Publié" : "Brouillon"}
                        </span>
                        {s.url && <ExternalLink className="w-3 h-3" />}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      <button onClick={() => setEditing(s)} data-testid={`edit-sponsor-${s.id}`} className="text-white/60 hover:text-[#E60012]">
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => remove(s.id)} data-testid={`del-sponsor-${s.id}`} className="text-white/60 hover:text-red-400">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })
      )}

      {editing && (
        <SponsorEditor
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
        />
      )}
    </div>
  );
};

const SponsorEditor = ({ initial, onClose, onSaved }) => {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) {
      setError("Image trop volumineuse (max 4 Mo)");
      return;
    }
    setF("logo_base64", await fileToBase64(f));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = {
        name: form.name,
        url: form.url || null,
        logo_base64: form.logo_base64 || null,
        tier: form.tier,
        category: form.tier === "custom" ? form.category || "" : "",
        sort_order: Number(form.sort_order) || 0,
        published: !!form.published,
      };
      if (form.id) {
        await api.put(`/admin/sponsors/${form.id}`, payload);
      } else {
        await api.post("/admin/sponsors", payload);
      }
      onSaved();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm overflow-y-auto p-4 flex items-start justify-center" data-testid="sponsor-editor-modal">
      <form onSubmit={submit} className="relative w-full max-w-lg my-10 bg-[#0f0f0f] border border-white/10 p-6">
        <button type="button" onClick={onClose} className="absolute top-3 right-3 w-9 h-9 bg-black/70 hover:bg-[#E60012] flex items-center justify-center" data-testid="sponsor-editor-close">
          <X className="w-4 h-4" />
        </button>
        <span className="font-condensed uppercase tracking-[0.3em] text-xs text-[#E60012]">
          {form.id ? "Modifier" : "Ajouter"}
        </span>
        <h2 className="mt-1 font-display text-2xl uppercase">
          {form.id ? "Modifier le commanditaire" : "Nouveau commanditaire"}
        </h2>

        <div className="mt-5 space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-24 h-24 bg-black border border-white/10 flex items-center justify-center overflow-hidden">
              {form.logo_base64 ? (
                <img src={form.logo_base64} alt="" className="max-w-full max-h-full object-contain p-2" />
              ) : (
                <Award className="w-8 h-8 text-[#E60012]/50" />
              )}
            </div>
            <label className="inline-flex items-center gap-2 px-3 py-2 border border-white/15 hover:border-[#E60012] cursor-pointer font-condensed uppercase tracking-[0.15em] text-xs">
              <ImageIcon className="w-3.5 h-3.5" /> Logo
              <input type="file" accept="image/*" onChange={onLogo} className="hidden" data-testid="sponsor-logo-input" />
            </label>
            {form.logo_base64 && (
              <button type="button" onClick={() => setF("logo_base64", null)} className="text-xs text-white/55 hover:text-red-400">
                Retirer
              </button>
            )}
          </div>

          <label className="block">
            <span className="text-xs text-white/60 font-condensed uppercase tracking-wider">Nom</span>
            <input
              type="text"
              required
              value={form.name}
              onChange={(e) => setF("name", e.target.value)}
              data-testid="sponsor-name-input"
              className="mt-1 w-full bg-black border border-white/10 px-3 py-2 outline-none focus:border-[#E60012]"
              placeholder="Nom de l'entreprise"
            />
          </label>

          <label className="block">
            <span className="text-xs text-white/60 font-condensed uppercase tracking-wider">Site web (optionnel)</span>
            <input
              type="url"
              value={form.url || ""}
              onChange={(e) => setF("url", e.target.value)}
              data-testid="sponsor-url-input"
              className="mt-1 w-full bg-black border border-white/10 px-3 py-2 outline-none focus:border-[#E60012]"
              placeholder="https://..."
            />
          </label>

          <div>
            <span className="text-xs text-white/60 font-condensed uppercase tracking-wider">Niveau</span>
            <div className="mt-1 grid grid-cols-2 sm:grid-cols-4 gap-2">
              {TIERS.map((t) => (
                <button
                  key={t.v}
                  type="button"
                  onClick={() => setF("tier", t.v)}
                  data-testid={`sponsor-tier-${t.v}`}
                  className={`px-3 py-2 font-condensed uppercase tracking-[0.15em] text-xs border ${
                    form.tier === t.v
                      ? "bg-[#E60012] text-white border-[#E60012]"
                      : "border-white/15 text-white/70 hover:border-[#E60012]"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {form.tier === "custom" && (
            <label className="block">
              <span className="text-xs text-white/60 font-condensed uppercase tracking-wider">Nom de la catégorie</span>
              <input
                type="text"
                value={form.category || ""}
                onChange={(e) => setF("category", e.target.value)}
                data-testid="sponsor-category-input"
                className="mt-1 w-full bg-black border border-white/10 px-3 py-2 outline-none focus:border-[#E60012]"
                placeholder="Présentateur, Officiel, Partenaire local..."
              />
            </label>
          )}

          <label className="block">
            <span className="text-xs text-white/60 font-condensed uppercase tracking-wider">Ordre d'affichage</span>
            <input
              type="number"
              value={form.sort_order || 0}
              onChange={(e) => setF("sort_order", e.target.value)}
              data-testid="sponsor-order-input"
              className="mt-1 w-32 bg-black border border-white/10 px-3 py-2 outline-none focus:border-[#E60012]"
            />
          </label>

          <label className="flex items-center gap-3 cursor-pointer select-none">
            <button
              type="button"
              onClick={() => setF("published", !form.published)}
              data-testid="sponsor-published-toggle"
              className={`w-10 h-5 rounded-full relative transition-colors ${form.published ? "bg-[#E60012]" : "bg-white/15"}`}
            >
              <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all ${form.published ? "left-5" : "left-0.5"}`} />
            </button>
            <span className="text-sm font-condensed uppercase tracking-[0.2em] text-white/70">
              {form.published ? "Publié" : "Brouillon"}
            </span>
          </label>
        </div>

        {error && <div className="mt-3 text-sm text-red-400">{error}</div>}

        <div className="mt-5 flex items-center gap-2">
          <button
            type="submit"
            disabled={saving}
            data-testid="sponsor-save-btn"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#E60012] hover:bg-[#ff1a30] disabled:opacity-50 font-condensed uppercase tracking-[0.15em] text-xs"
          >
            <Save className="w-3.5 h-3.5" /> {saving ? "..." : form.id ? "Mettre à jour" : "Publier"}
          </button>
          <button type="button" onClick={onClose} className="px-4 py-2 border border-white/15 hover:border-white/40 font-condensed uppercase tracking-[0.15em] text-xs">
            Annuler
          </button>
        </div>
      </form>
    </div>
  );
};

export default SponsorsPanel;
